import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { connect, useDispatch } from 'react-redux' // Added useDispatch
import { stopByteCondition } from '../../../utils/utils'
import {
    livePortsNumber,
    livePortsNumberMoki,
    compName_character,
} from '../../logic/panels/helpers/LiveButton'
import { connectSerialDeviceIntermediate } from '../../WebSerialConnection/SerialUSBConnect'
import { webSerialConnect, deviceMismatch } from '../../../redux/actions'
import renderImage from '../../../source/importImg'

// Accepts either a legacy board component type (toki_board) or a resolved
// device name (Toki / Poki / Moki) passed via the boardType prop.
const BOARD_TO_DEVICE = {
    toki_board: 'Toki',
    board: 'Toki',
    Toki: 'Toki',
    Poki: 'Poki',
    Moki: 'Moki',
}

const PORT_NAME_MAP = {
    servo_motor1: 'S1',
    servo_motor2: 'S2',
    M3: 'M2',
}

const COMPONENT_RANGES = {
    mini_geared_motor: { min: -100, max: 100 },
    geared_motor: { min: -100, max: 100 },
    dc_motor: { min: -100, max: 100 },
    dynamex_motor: { min: -100, max: 100 },
    servo_motor: { min: 0, max: 180 },
    servo_motor_270: { min: 0, max: 270 },
    servo_motor_sg90: { min: 0, max: 180 },
    servo_motor_360: { min: -100, max: 100 },
    led: { min: 0, max: 100 },
    led_1c: { min: 0, max: 100 },
    pwm_3_pin_output: { min: 0, max: 100 },
    digital_3_pin_output: { min: 0, max: 255 },
    PWM_3_Pin_Output: { min: 0, max: 100 },
}

function ByteRecorderPopup({
    initialValue,
    isOpen,
    onClose,
    onRecord,
    componentType,
    port,
    boardType,
    worker,
    webSerial,
    device,
    socketData,
    webSerialConnect,
    deviceMismatch,
    PortConnections,
    internalaccessories,
}) {
    const dispatch = useDispatch() // Initialize Redux dispatcher
    const [sliderValue, setSliderValue] = useState(0)
    const [rgbValues, setRgbValues] = useState({ r: 100, g: 100, b: 100 })
    const [isLive, setIsLive] = useState(false)
    const [lastSentBytes, setLastSentBytes] = useState(null)
    const [isDeviceMismatch, setIsDeviceMismatch] = useState(false)
    const [showErrorMsg, setShowErrorMsg] = useState(false)

    const sentInitialRef = useRef(false)
    const liveStartedRef = useRef(false) // <-- ADD THIS
    const debounceTimer = useRef(null) // <-- ADD THIS for RGB sliding

    const connectedDevice =
        sessionStorage.getItem('connectedDevice') ||
        BOARD_TO_DEVICE[boardType] ||
        'Toki'
    const compType = componentType?.toLowerCase().replace(/ /g, '_') || ''
    const range = COMPONENT_RANGES[compType] || { min: 0, max: 100 }

    const isDualPort = [
        'mini_geared_motor',
        'geared_motor',
        'dc_motor',
        'dynamex_motor',
    ].includes(compType)

    const { port1, port2 } = useMemo(() => {
        if (!isDualPort || !port) return { port1: port, port2: null }
        const match = port.match(/^([A-Za-z]+)(\d+)$/)
        if (match) {
            const letter = match[1]
            const num = parseInt(match[2], 10)
            return { port1: port, port2: `${letter}${num + 1}` }
        }
        return { port1: `${port}1`, port2: `${port}2` }
    }, [port, isDualPort])

    const getPortNumberFor = useCallback(
        (targetPort) => {
            return (
                livePortsNumber?.[connectedDevice]?.[targetPort] ||
                livePortsNumber?.Ace?.[targetPort] ||
                0
            )
        },
        [connectedDevice]
    )

    const getPortNumber = useCallback(() => {
        return getPortNumberFor(port)
    }, [port, getPortNumberFor])

    const getCompChar = useCallback(() => {
        return compName_character?.[compType] || 'P'.charCodeAt(0)
    }, [compType])

    const buildLiveStartBytes = useCallback(() => {
        // Replicate the exact legacy logic from LiveButton.js handelClick
        let data = []
        if (['Moki', 'Poki', 'Toki'].includes(connectedDevice)) {
            let len =
                Object.keys(livePortsNumber[connectedDevice] || {})?.length + 2
            data = Array(len || 20).fill(79)
        } else {
            data = Array(20).fill(79)
        }
        data[0] = 'L'.charCodeAt(0) // 76
        data[1] = 'S'.charCodeAt(0) // 83

        // Global port loop: inject component char codes at correct indices
        const ports = PortConnections || {}
        Object.keys(ports).forEach((portKey) => {
            let portCompType = ports[portKey]?.type

            // Atr exception: ignore C1 and C2
            if (connectedDevice === 'Atr' && ['C1', 'C2'].includes(portKey)) {
                return
            }

            if (
                [
                    'led_1c',
                    'led',
                    'geared_motor',
                    'mini_geared_motor',
                    'dc_motor',
                    'dynamex_motor',
                    'servo_motor',
                    'servo_motor_270',
                    'servo_motor_sg90',
                    'servo_motor_360',
                    'PWM_3_Pin_Output',
                ].includes(portCompType)
            ) {
                // Moki exception: use livePortsNumberMoki dictionary
                if (connectedDevice === 'Moki') {
                    data[livePortsNumberMoki[portKey] + 1] =
                        compName_character[portCompType]
                } else {
                    data[
                        (livePortsNumber[connectedDevice]?.[portKey] ||
                            livePortsNumber.Ace?.[portKey]) + 1
                    ] = compName_character[portCompType]
                }
            }
        })

        // Atr internal motors
        const internalAcc = internalaccessories || {}
        Object.keys(internalAcc).forEach((interNalComponent) => {
            if (interNalComponent.includes('isAtrMotor')) {
                if (
                    interNalComponent === 'isAtrMotor1' &&
                    internalAcc.isAtrMotor1
                ) {
                    data[livePortsNumber.Ace?.['AtrMotor1M1'] + 1] =
                        'P'.charCodeAt(0)
                    data[livePortsNumber.Ace?.['AtrMotor1M2'] + 1] =
                        'P'.charCodeAt(0)
                } else if (
                    interNalComponent === 'isAtrMotor2' &&
                    internalAcc.isAtrMotor2
                ) {
                    data[livePortsNumber.Ace?.['AtrMotor2M3'] + 1] =
                        'P'.charCodeAt(0)
                    data[livePortsNumber.Ace?.['AtrMotor2M4'] + 1] =
                        'P'.charCodeAt(0)
                } else if (
                    interNalComponent === 'isAtrMotor3' &&
                    internalAcc.isAtrMotor3
                ) {
                    data[livePortsNumber.Ace?.['AtrMotor3E1'] + 1] =
                        'P'.charCodeAt(0)
                    data[livePortsNumber.Ace?.['AtrMotor3E2'] + 1] =
                        'P'.charCodeAt(0)
                }
            }
        })

        // Klaw exception: humanoid servo ports
        if (connectedDevice === 'Klaw') {
            try {
                const sessionPortData = JSON.parse(
                    sessionStorage.getItem('humanoidPortData')
                )
                if (sessionPortData?.['4b']?.isPortSelected) {
                    for (let i = 13; i <= 17; i++) {
                        data[i + 1] = 'S'.charCodeAt(0)
                    }
                }
            } catch (e) {
                console.log('ERROR parsing humanoidPortData', e)
            }
        }

        // Force the current port into the Live Start array just in case it's not in Redux yet
        const charCode = getCompChar()
        if (isDualPort) {
            const p1Num = getPortNumberFor(port1)
            const p2Num = getPortNumberFor(port2)
            if (p1Num !== undefined) data[p1Num + 1] = charCode
            if (p2Num !== undefined) data[p2Num + 1] = charCode
        } else {
            const pNum = getPortNumberFor(port)
            if (pNum !== undefined) data[pNum + 1] = charCode
        }

        return data
    }, [
        connectedDevice,
        PortConnections,
        internalaccessories,
        isDualPort,
        port,
        port1,
        port2,
        getCompChar,
        getPortNumberFor,
    ])

    // 1. ALL HOOKS MUST STAY ABOVE THE RETURN STATEMENT
    useEffect(() => {
        if (isOpen) {
            if (typeof initialValue === 'object' && initialValue !== null) {
                setRgbValues({
                    r: initialValue.r ?? 100,
                    g: initialValue.g ?? 100,
                    b: initialValue.b ?? 100,
                })
                setSliderValue(0)
            } else {
                setSliderValue(initialValue || 0)
                setRgbValues({ r: 100, g: 100, b: 100 })
            }
            setLastSentBytes(null)
        }
    }, [isOpen, initialValue])

    // Send the "Live Start" handshake EXACTLY ONCE when the popup opens and connects
    useEffect(() => {
        if (
            isOpen &&
            worker &&
            webSerial?.isConnected &&
            !liveStartedRef.current
        ) {
            liveStartedRef.current = true

            const initLiveMode = async () => {
                const deviceVersion = sessionStorage.getItem('deviceVersion')
                const sendStopBytes = stopByteCondition({
                    connectedDevice,
                    deviceVersion,
                })

                // 1. Send Stop
                worker.postMessage({ type: 'writeArray', value: sendStopBytes })
                await new Promise((r) => setTimeout(r, 100))

                // 2. Send Live Start
                const startData = buildLiveStartBytes()
                worker.postMessage({ type: 'writeArray', value: startData })
            }

            initLiveMode()
        }

        // Reset the ref when the popup closes so it can re-initialize next time
        if (!isOpen) {
            liveStartedRef.current = false
        }
    }, [isOpen, worker, webSerial, connectedDevice, buildLiveStartBytes])

    const buildLiveWriteBytes = useCallback(
        (value) => {
            const charCode = getCompChar()

            if (isDualPort) {
                let p1Val = 0
                let p2Val = 0

                if (value < 0) {
                    p1Val =
                        compType === 'dynamex_motor'
                            ? Math.round(Math.abs(value) * 0.8)
                            : Math.abs(value)
                } else if (value > 0) {
                    p2Val =
                        compType === 'dynamex_motor'
                            ? Math.round(value * 0.8)
                            : value
                }

                return [
                    [
                        'L'.charCodeAt(0),
                        'W'.charCodeAt(0),
                        charCode,
                        getPortNumberFor(port1),
                        p1Val,
                    ],
                    [
                        'L'.charCodeAt(0),
                        'W'.charCodeAt(0),
                        charCode,
                        getPortNumberFor(port2),
                        p2Val,
                    ],
                ]
            } else {
                let adjustedValue = value
                if (compType === 'servo_motor_270') {
                    adjustedValue = Math.round(0.6667 * value)
                } else if (compType === 'servo_motor_360') {
                    adjustedValue = 100 + value
                }

                return [
                    [
                        'L'.charCodeAt(0),
                        'W'.charCodeAt(0),
                        charCode,
                        getPortNumberFor(port),
                        adjustedValue,
                    ],
                ]
            }
        },
        [
            isDualPort,
            compType,
            getCompChar,
            getPortNumberFor,
            port1,
            port2,
            port,
        ]
    )

    const buildRGBLiveWriteBytes = useCallback((r, g, b) => {
        return [[76, 87, 76, 75, 0, r, g, b]]
    }, [])

    const sendRGBLiveBytes = useCallback(
        (r, g, b) => {
            const bytes = [76, 87, 76, 75, 0, r, g, b]
            worker.postMessage({ type: 'writeArray', value: bytes })
            setLastSentBytes([[...bytes]])
        },
        [worker]
    )

    const requestConnection = async () => {
        if (!webSerial.isConnected) {
            let res = await connectSerialDeviceIntermediate(
                { isAutomatic: false },
                worker,
                socketData
            )
            if (res !== undefined) {
                if (res.matchedObj_with_Version?.firmwareUnsupported) {
                    // Unsupported firmware must never be accepted as the
                    // connected device — show the firmware update dialog
                    // directly instead of the generic device-mismatch popup.
                    const mismatchedDevice = res.matchedObj_with_Version
                    dispatch({
                        type: 'SHOW_FIRMWARE_UPDATE_DIALOG',
                        payload: {
                            currentVersion: mismatchedDevice.currentVersion,
                            latestVersion: mismatchedDevice.minFirmware,
                            deviceName: mismatchedDevice.id,
                            firmwareCheck: { mandatory: true },
                        },
                    })
                } else if (!res.isPortOpen) {
                    deviceMismatch({
                        deviceMismatched: res.matchedObj_with_Version,
                        isMemoryPresent: res.isMemoryPresent,
                        isArmError: res.isArmError,
                        plodeNativeData: {
                            ...device.plodeNativeData,
                            mountPoint: res.mountPoint,
                        },
                    })
                    setIsDeviceMismatch(true)
                } else {
                    setIsDeviceMismatch(false)
                    deviceMismatch({
                        deviceMismatched: {},
                        isMemoryPresent: res.isMemoryPresent,
                        isArmError: res.isArmError,
                        plodeNativeData: {
                            ...device.plodeNativeData,
                            mountPoint: res.mountPoint,
                        },
                    })
                }
            }

            if (res) webSerialConnect({ port: res.port })
            else setShowErrorMsg(true)
        }
    }

    const isRGBComponent = compType.includes('rgb') || compType.includes('eye')

    const handleLiveToggle = async () => {
        if (!webSerial?.isConnected) {
            window.alert(`Connect your ${connectedDevice} board first!`)
            return
        }

        if (isLive) {
            setIsLive(false)
            // Turn OFF
            if (isRGBComponent) {
                worker.postMessage({
                    type: 'writeArray',
                    value: [76, 87, 76, 75, 0, 0, 0, 0],
                })
            } else {
                const stopSequence = buildLiveWriteBytes(0)
                for (const bytes of stopSequence) {
                    worker.postMessage({ type: 'writeArray', value: bytes })
                    await new Promise((r) => setTimeout(r, 100))
                }
            }
            return
        }

        // Turn ON
        dispatch({ type: 'DISABLE_AFTER_SETTING_BYTE', payload: false })
        setIsLive(true)

        // ---> THE FIX: Send the Live Start handshake before the Live Write bytes <---
        const startData = buildLiveStartBytes()
        worker.postMessage({ type: 'writeArray', value: startData })
        await new Promise((r) => setTimeout(r, 150))
        // -------------------------------------------------------------------------

        if (isRGBComponent) {
            sendRGBLiveBytes(rgbValues.r, rgbValues.g, rgbValues.b)
        } else {
            const writeSequence = buildLiveWriteBytes(sliderValue)
            setLastSentBytes(writeSequence)
            for (const bytes of writeSequence) {
                worker.postMessage({ type: 'writeArray', value: bytes })
                await new Promise((r) => setTimeout(r, 100))
            }
        }
    }

    const handleSliderChange = async (e) => {
        const val = Number(e.target.value)
        setSliderValue(val)

        if (!isLive || !webSerial?.isConnected) return

        if (isRGBComponent) {
            // DEBOUNCE THE RGB LIVE BYTES
            if (debounceTimer.current) clearTimeout(debounceTimer.current)
            debounceTimer.current = setTimeout(() => {
                sendRGBLiveBytes(rgbValues.r, rgbValues.g, rgbValues.b)
            }, 150)
        } else {
            // STANDARD MOTORS/LEDS
            const byteSequence = buildLiveWriteBytes(val)
            setLastSentBytes(byteSequence)
            for (const bytes of byteSequence) {
                worker.postMessage({ type: 'writeArray', value: bytes })
                await new Promise((r) => setTimeout(r, 100))
            }
        }
    }

    // --- Clean RGB LW bytes: [L, W, L, K, 0, R, G, B] ---
    const handleRgbSliderChange = (channel, val) => {
        const newRgb = { ...rgbValues, [channel]: val }
        setRgbValues(newRgb)
        if (!isLive || !webSerial?.isConnected) return
        if (debounceTimer.current) clearTimeout(debounceTimer.current)
        debounceTimer.current = setTimeout(() => {
            sendRGBLiveBytes(newRgb.r, newRgb.g, newRgb.b)
        }, 150)
    }

    const handleRecord = () => {
        const bytes = isRGBComponent
            ? buildRGBLiveWriteBytes(rgbValues.r, rgbValues.g, rgbValues.b)
            : buildLiveWriteBytes(sliderValue)
        const sliderVal = isRGBComponent ? { ...rgbValues } : sliderValue
        const fullEnableBytesArray = buildLiveStartBytes()

        // Pass the full LS enable bytes array up to PlayAdmin
        onRecord(bytes, sliderVal, fullEnableBytesArray)
    }

    const handleClose = async () => {
        if (isLive && webSerial?.isConnected) {
            if (isRGBComponent) {
                worker.postMessage({
                    type: 'writeArray',
                    value: [76, 87, 76, 75, 0, 0, 0, 0],
                })
            } else {
                const stopSequence = buildLiveWriteBytes(0)
                for (const bytes of stopSequence) {
                    worker.postMessage({ type: 'writeArray', value: bytes })
                    await new Promise((r) => setTimeout(r, 100))
                }
            }
        }

        setIsLive(false)
        setLastSentBytes(null)
        setSliderValue(0)
        onClose()
    }

    const formatBytes = (bytes) => {
        if (!bytes) return '—'
        if (!Array.isArray(bytes[0])) {
            return `[${bytes.join(', ')}]`
        }
        return `[${bytes.map((arr) => `[${arr.join(', ')}]`).join(', ')}]`
    }

    // 2. EARLY RETURN MOVED BELOW HOOKS
    if (!isOpen) return null

    return (
        <div
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0,0,0,0.5)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 9999,
            }}
            onClick={handleClose}
        >
            <div
                style={{
                    background: '#fff',
                    borderRadius: '12px',
                    padding: '30px',
                    width: '520px',
                    maxWidth: '90vw',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '20px',
                    }}
                >
                    <h3 style={{ margin: 0, color: '#1a73e8' }}>
                        Byte Recorder
                    </h3>
                    <img
                        src={
                            webSerial.isConnected
                                ? renderImage('UsbOn')
                                : renderImage('UsbOff')
                        }
                        onClick={requestConnection}
                        alt="USB"
                        title={
                            webSerial.isConnected
                                ? 'USB connected'
                                : 'Click to connect USB device'
                        }
                        style={{
                            width: 'auto',
                            height: '32px',
                            cursor: webSerial.isConnected
                                ? 'default'
                                : 'pointer',
                        }}
                    />
                </div>

                {showErrorMsg && (
                    <div
                        style={{
                            background: '#fef2f2',
                            border: '1px solid #fca5a5',
                            color: '#dc2626',
                            padding: '10px 14px',
                            borderRadius: '6px',
                            marginBottom: '15px',
                            fontSize: '0.85rem',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <span>
                            Error connecting device. Please disconnect and try
                            again.
                        </span>
                        <button
                            onClick={() => setShowErrorMsg(false)}
                            style={{
                                background: 'none',
                                border: 'none',
                                color: '#dc2626',
                                cursor: 'pointer',
                                fontWeight: 'bold',
                                fontSize: '1rem',
                            }}
                        >
                            ✕
                        </button>
                    </div>
                )}

                <div
                    style={{
                        background: '#f4f4f9',
                        padding: '12px',
                        borderRadius: '6px',
                        marginBottom: '20px',
                        fontSize: '0.85rem',
                    }}
                >
                    <div>
                        <strong>Board:</strong>{' '}
                        {BOARD_TO_DEVICE[boardType] || boardType}
                    </div>
                    <div>
                        <strong>Component:</strong> {componentType || '—'}
                    </div>
                    <div>
                        <strong>Port:</strong> {PORT_NAME_MAP[port] || '—'}
                    </div>
                    <div>
                        <strong>Range:</strong> {range.min} – {range.max}
                    </div>
                </div>

                <div style={{ marginBottom: '20px' }}>
                    {isRGBComponent ? (
                        // RGB: three independent sliders (R, G, B)
                        <>
                            <label
                                style={{
                                    display: 'block',
                                    fontWeight: 'bold',
                                    marginBottom: '8px',
                                }}
                            >
                                RGB Value
                            </label>
                            {[
                                ['r', '#d32f2f', 'Red'],
                                ['g', '#388e3c', 'Green'],
                                ['b', '#1565c0', 'Blue'],
                            ].map(([ch, color, label]) => (
                                <div key={ch} style={{ marginBottom: '10px' }}>
                                    <label
                                        style={{
                                            display: 'block',
                                            fontSize: '0.85rem',
                                            color,
                                            marginBottom: '4px',
                                        }}
                                    >
                                        {label}: {rgbValues[ch]}
                                    </label>
                                    <input
                                        type="range"
                                        min={0}
                                        max={100}
                                        value={rgbValues[ch]}
                                        onChange={(e) =>
                                            handleRgbSliderChange(
                                                ch,
                                                Number(e.target.value)
                                            )
                                        }
                                        style={{
                                            width: '100%',
                                            accentColor: color,
                                        }}
                                    />
                                    <div
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            fontSize: '0.8rem',
                                            color: '#888',
                                        }}
                                    >
                                        <span>0</span>
                                        <span>100</span>
                                    </div>
                                </div>
                            ))}
                        </>
                    ) : (
                        // Standard: single slider
                        <>
                            <label
                                style={{
                                    display: 'block',
                                    fontWeight: 'bold',
                                    marginBottom: '8px',
                                }}
                            >
                                Value: {sliderValue}
                            </label>
                            <input
                                type="range"
                                min={range.min}
                                max={range.max}
                                value={sliderValue}
                                onChange={handleSliderChange}
                                style={{ width: '100%' }}
                            />
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    fontSize: '0.8rem',
                                    color: '#888',
                                }}
                            >
                                <span>{range.min}</span>
                                <span>{range.max}</span>
                            </div>
                        </>
                    )}
                </div>

                <div
                    style={{
                        display: 'flex',
                        gap: '10px',
                        alignItems: 'center',
                        marginBottom: '15px',
                    }}
                >
                    <button
                        onClick={handleLiveToggle}
                        style={{
                            padding: '8px 20px',
                            background: isLive ? '#ef4444' : '#34a853',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontWeight: 'bold',
                        }}
                    >
                        {isLive ? 'Stop Live' : 'Live'}
                    </button>
                    <span
                        style={{
                            fontSize: '0.85rem',
                            color: isLive ? '#34a853' : '#888',
                            fontWeight: isLive ? 'bold' : 'normal',
                        }}
                    >
                        {isLive
                            ? 'Sending data to device...'
                            : 'Click Live to start sending'}
                    </span>
                </div>

                <div
                    style={{
                        background: '#1e1e1e',
                        color: '#4ec9b0',
                        padding: '12px 15px',
                        borderRadius: '6px',
                        fontFamily: 'monospace',
                        fontSize: '0.9rem',
                        marginBottom: '20px',
                        minHeight: '40px',
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    <span style={{ color: '#888', marginRight: '8px' }}>
                        Bytes:
                    </span>
                    {formatBytes(
                        lastSentBytes ||
                            (isRGBComponent
                                ? buildRGBLiveWriteBytes(
                                      rgbValues.r,
                                      rgbValues.g,
                                      rgbValues.b
                                  )
                                : buildLiveWriteBytes(sliderValue))
                    )}
                </div>

                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: '10px',
                    }}
                >
                    <button
                        onClick={handleClose}
                        style={{
                            padding: '8px 20px',
                            background: '#f0f0f0',
                            border: '1px solid #ccc',
                            borderRadius: '6px',
                            cursor: 'pointer',
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleRecord}
                        style={{
                            padding: '8px 20px',
                            background: '#1a73e8',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontWeight: 'bold',
                        }}
                    >
                        Record
                    </button>
                </div>
            </div>
        </div>
    )
}

const mapStateToProps = (state) => ({
    worker: state.worker,
    webSerial: state.webSerial,
    device: state.device,
    socketData: state.socketData,
    PortConnections: state.assembly?.PortConnections || {},
    internalaccessories: state.concept?.internalaccessories || {},
})

const mapDispatchToProps = (dispatch) => ({
    webSerialConnect: (data) => dispatch(webSerialConnect(data)),
    deviceMismatch: (data) => dispatch(deviceMismatch(data)),
})

export default connect(mapStateToProps, mapDispatchToProps)(ByteRecorderPopup)
